import Router from 'next/router';
import { useTranslation } from 'next-i18next';
import PropTypes from 'prop-types';
import { useCallback, useEffect, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import ForgotPassword from '@/components/authentication/ForgotPassword';
import CallToActionContainer from '@/components/common/callToAction/CallToActionContainer';
import FormField from '@/components/common/form/FormField';
import SocialLoginButtons from '@/components/common/Login/SocialLoginButtons';
import { NS } from '@/constants';
import { FEATURES } from '@/constants/features';
import { PAGES } from '@/constants/pages';
import { useFeature } from '@/hooks';
import { useAppDispatch, useAppSelector } from '@/redux';
import { clearAuthStatus, connectUser } from '@/redux/slices/auth.slice';
import { addApiErrorToast } from '@/redux/slices/toast.slice';
import { CallToActionKind } from '@/types';
import styles from './LoginForm.module.scss';

export const LoginForm = ({ onClose, showSubscribe = true, withModal = false }) => {
	const signinStatus = useAppSelector(({ auth }) => auth.status.signin);
	const storeConf = useAppSelector(({ root }) => root.storeConf);
	const appleSignInFeatureEnabled = useFeature(storeConf, FEATURES.APPLE_SIGN_IN);
	const googleLoginFeatureEnabled = useFeature(storeConf, FEATURES.GOOGLE_LOGIN);
	const [forgotPassword, setForgotPassword] = useState(false);
	const { t } = useTranslation(NS.COMMON);
	const dispatch = useAppDispatch();

	const methods = useForm();
	const { handleSubmit } = methods;

	useEffect(() => {
		if (signinStatus) {
			if (signinStatus.error) {
				dispatch(addApiErrorToast({ error: signinStatus.error }));
				dispatch(clearAuthStatus({ name: 'signin' }));
			} else if (signinStatus.success) {
				if (signinStatus.redirectUrl) {
					Router.push(signinStatus.redirectUrl);
				}
				dispatch(clearAuthStatus({ name: 'signin' }));
			}
		}
	}, [signinStatus]);

	const toggleForgotClick = (e) => {
		e.preventDefault();
		setForgotPassword(!forgotPassword);
	};

	const computeReturnUrl = () => {
		if (storeConf.privateWebsite) {
			return PAGES.HOME;
		} else if (window.location.href.endsWith('#')) {
			return window.location.href.slice(0, -1);
		} else {
			return window.location.href;
		}
	};

	const onSubmit = useCallback((data) => {
		dispatch(
			connectUser({
				email: data.email,
				password: data.password,
				redirectUrl: computeReturnUrl(),
			}),
		);
	}, []);

	const handleSubscribe = () => {
		onClose?.();
		Router.push(`${PAGES.AUTH}?isSignUp=true&origin=${window.location.pathname}`);
	};

	return (
		<div data-rbf="login-form" className={styles.container} data-test-id="login-form">
			{forgotPassword ? (
				<>
					<div className="title">{t('forms.retrievePassword')}</div>
					<ForgotPassword handleCancel={toggleForgotClick} />
				</>
			) : (
				<>
					{!withModal && <div className="title">{t('forms.login.title')}</div>}
					<FormProvider {...methods}>
						<form onSubmit={handleSubmit(onSubmit)} data-test-id="formValidator-login-form">
							<FormField
								id="login-form-email"
								name="email"
								type="email"
								label={t('forms.email')}
								data-test-id="login-form-email"
								required={t('forms.requiredField')}
								pattern={{ value: /\S+@\S+\.\S+/, message: t('forms.validEmail') }}
							/>
							<FormField
								id="login-form-password"
								name="password"
								type="password"
								label={t('forms.password')}
								data-test-id="login-form-password"
								required={t('forms.requiredField')}
							/>
							<a
								className={styles.forgotten}
								data-rbf="forgot-password"
								data-test-id="forgot-password-link"
								onClick={toggleForgotClick}
								aria-label={t('forms.forgotPassword')}
							>
								{t('forms.forgotPassword')}
							</a>
							<CallToActionContainer id="login-form-submit" type="submit" className="mil-mt-2" fullWidth>
								{t('account.login')}
							</CallToActionContainer>
						</form>
					</FormProvider>
					{(appleSignInFeatureEnabled || googleLoginFeatureEnabled) && (
						<>
							<span className={styles.text}>{t('or').toUpperCase()}</span>
							<div className={styles.socialLogin}>
								<SocialLoginButtons fullWidth />
							</div>
						</>
					)}
					{showSubscribe && (
						<>
							<div className={styles['sub-title']}>{t('account.subscribe.title')}</div>
							<div className={styles.submit}>
								<CallToActionContainer
									id="login-form-subscribe"
									onClick={handleSubscribe}
									kind={CallToActionKind.SECONDARY}
									fullWidth
								>
									{t('account.subscribe.btn')}
								</CallToActionContainer>
							</div>
						</>
					)}
				</>
			)}
		</div>
	);
};

LoginForm.propTypes = {
	onClose: PropTypes.func,
	withModal: PropTypes.bool,
	showSubscribe: PropTypes.bool,
};

export default LoginForm;
